import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { X, Loader2, Link, Server, Key, Globe, User, Download, Copy, Check } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  SSOProvider,
  CreateSSOProviderData,
  UpdateSSOProviderData,
} from "../../services/sso-providers.service";

interface SSOProviderFormProps {
  isOpen: boolean;
  onClose: () => void;
  provider: SSOProvider | null;
  onSubmit: (
    data: CreateSSOProviderData | UpdateSSOProviderData
  ) => Promise<void>;
  isLoading: boolean;
}

export default function SSOProviderForm({
  isOpen,
  onClose,
  provider,
  onSubmit,
  isLoading,
}: SSOProviderFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateSSOProviderData>({
    mode: "onChange",
  });

  const [wellKnownUrl, setWellKnownUrl] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [copied, setCopied] = useState(false);

  const isEdit = !!provider;
  const providerNameValue = watch("provider_name") || "";
  const redirectUrl = `${import.meta.env.VITE_DMS_API}/dms-v2-srv/social-login/handle/${providerNameValue}/token`;

  const fetchWellKnownConfig = async () => {
    if (!wellKnownUrl.trim()) {
      toast.error("Please enter a well-known URL");
      return;
    }

    try {
      setIsFetching(true);
      const response = await fetch(wellKnownUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }

      const config = await response.json();

      // Auto-populate fields from well-known response
      if (config.issuer) {
        setValue("issuer", config.issuer);
      }
      if (config.authorization_endpoint) {
        setValue("authorization_endpoint", config.authorization_endpoint);
      }
      if (config.token_endpoint) {
        setValue("token_endpoint", config.token_endpoint);
      }
      if (config.userinfo_endpoint) {
        setValue("userinfo_endpoint", config.userinfo_endpoint);
      }

      toast.success("Configuration loaded successfully!");
    } catch (error: any) {
      console.error("Error fetching well-known config:", error);
      toast.error(`Failed to fetch configuration: ${error.message}`);
    } finally {
      setIsFetching(false);
    }
  };

  const handleCopyRedirectUrl = async () => {
    try {
      await navigator.clipboard.writeText(redirectUrl);
      setCopied(true);
      toast.success("Redirect URL copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy URL");
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (provider) {
        reset({
          issuer: provider.issuer,
          authorization_endpoint: provider.authorization_endpoint,
          userinfo_endpoint: provider.userinfo_endpoint,
          token_endpoint: provider.token_endpoint,
          provider_name: provider.provider_name,
          display_name: provider.display_name,
          logo_url: provider.logo_url,
          client_id: provider.client_id,
          client_secret: "", // Don't pre-populate secret for security
          enable: provider.enable,
        });
        setWellKnownUrl("");
      } else {
        reset({
          issuer: "",
          authorization_endpoint: "",
          userinfo_endpoint: "",
          token_endpoint: "",
          provider_name: "",
          display_name: "",
          logo_url: "",
          client_id: "",
          client_secret: "",
          enable: true,
        });
        setWellKnownUrl("");
      }
    }
  }, [isOpen, provider, reset]);

  const handleFormSubmit = (data: CreateSSOProviderData) => {
    // Always set standard_type to openid_connect
    const submitData = {
      ...data,
      standard_type: "openid_connect"
    };
    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? "Edit SSO Provider" : "Add New SSO Provider"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="p-6 space-y-6"
        >
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Basic Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Provider Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Provider Name *
                </label>
                <div className="relative">
                  <Server className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    {...register("provider_name", {
                      required: "Provider name is required",
                      pattern: {
                        value: /^[a-zA-Z0-9_]+$/,
                        message:
                          "Provider name can only contain letters, numbers, and underscores",
                      },
                    })}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., github, google, microsoft"
                  />
                </div>
                {errors.provider_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.provider_name.message}
                  </p>
                )}
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    {...register("display_name", {
                      required: "Display name is required",
                      pattern: {
                        value: /^[A-Za-z0-9\s_-]*$/,
                        message: "Only alphabets, numbers, spaces, hyphens and underscores are allowed"
                      }
                    })}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., GitHub Login, Google SSO"
                  />
                </div>
                {errors.display_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.display_name.message}
                  </p>
                )}
              </div>
            </div>


            {/* Logo URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo URL
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  {...register("logo_url")}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/logo.png"
                />
              </div>
              {errors.logo_url && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.logo_url.message}
                </p>
              )}
            </div>
          </div>

          {/* Well-Known URL Configuration */}
          <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-600" />
              Auto-Configure from Well-Known URL
            </h3>
            <p className="text-sm text-gray-600">
              Enter the well-known configuration URL to automatically populate the endpoint fields below.
            </p>

            <div className="flex gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="url"
                    value={wellKnownUrl}
                    onChange={(e) => setWellKnownUrl(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://your-provider.com/.well-known/openid_configuration"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={fetchWellKnownConfig}
                disabled={isFetching || !wellKnownUrl.trim()}
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
              >
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Fetching...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>Fetch Config</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* OAuth Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              OAuth Configuration
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client ID *
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    {...register("client_id", {
                      required: "Client ID is required",
                      pattern: {
                        value: /^[A-Za-z0-9._-]+$/,
                        message: "Client ID can only contain letters, numbers, hyphens, underscores and periods"
                      }
                    })}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="OAuth Client ID"
                  />
                </div>
                {errors.client_id && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.client_id.message}
                  </p>
                )}
              </div>

              {/* Client Secret */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Secret {!isEdit && "*"}
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    {...register("client_secret", {
                      required: isEdit ? false : "Client secret is required",
                      pattern: {
                        value: /^(?!\s)(?!.*\s$)[^<>]*$/,
                        message: "Client secret contains invalid characters"
                      }
                    })}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={isEdit ? "••••••••••••••••" : "OAuth Client Secret"}
                  />
                </div>
                {isEdit && (
                  <p className="mt-1 text-sm text-amber-600 flex items-start gap-1">
                    <span className="text-amber-500">⚠️</span>
                    For security, the existing secret is not displayed. Enter a new secret to update it, or leave blank to keep the current one.
                  </p>
                )}
                {errors.client_secret && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.client_secret.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Redirect URL Configuration */}
          <div className="space-y-4 bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Link className="w-5 h-5 text-green-600" />
              Redirect URL (For OAuth Provider Setup)
            </h3>
            <p className="text-sm text-gray-600">
              Use this URL in your OAuth provider's configuration as the redirect/callback URL.
            </p>

            <div className="flex gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={redirectUrl}
                    readOnly
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-default select-all"
                    placeholder="Enter provider name above to see the redirect URL"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleCopyRedirectUrl}
                disabled={!providerNameValue.trim()}
                className="px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 font-medium disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                title="Copy redirect URL to clipboard"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy URL</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Endpoints Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Endpoint Configuration
            </h3>

            {/* Issuer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Issuer URL *
              </label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  {...register("issuer", {
                    required: "Issuer URL is required",
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: "Please enter a valid URL",
                    },
                  })}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://oauth.provider.com"
                />
              </div>
              {errors.issuer && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.issuer.message}
                </p>
              )}
            </div>

            {/* Authorization Endpoint */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Authorization Endpoint *
              </label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  {...register("authorization_endpoint", {
                    required: "Authorization endpoint is required",
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: "Please enter a valid URL",
                    },
                  })}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://oauth.provider.com/authorize"
                />
              </div>
              {errors.authorization_endpoint && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.authorization_endpoint.message}
                </p>
              )}
            </div>

            {/* Token Endpoint */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Token Endpoint *
              </label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  {...register("token_endpoint", {
                    required: "Token endpoint is required",
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: "Please enter a valid URL",
                    },
                  })}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://oauth.provider.com/token"
                />
              </div>
              {errors.token_endpoint && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.token_endpoint.message}
                </p>
              )}
            </div>

            {/* User Info Endpoint */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Info Endpoint *
              </label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="url"
                  {...register("userinfo_endpoint", {
                    required: "User info endpoint is required",
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: "Please enter a valid URL",
                    },
                  })}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://oauth.provider.com/userinfo"
                />
              </div>
              {errors.userinfo_endpoint && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.userinfo_endpoint.message}
                </p>
              )}
            </div>
          </div>

          {/* Enable Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              {...register("enable")}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              Enable this SSO provider
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isEdit ? "Updating..." : "Creating..."}</span>
                </>
              ) : (
                <span>{isEdit ? "Update Provider" : "Create Provider"}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
